import { useMemo } from 'react';

interface MSRPInputs {
  numberOfApplications: number;
  scanSchedule: 'daily' | 'weekly' | 'monthly' | string;
  supportTier?: 'foundational' | 'professional' | 'ultimate' | string;
  numberOfAssets?: number;
}

interface MSRPResult {
  msrp: number;
  breakdown: {
    platformFee: number;
    fqdnCost: number;
    assetCost: number;
    supportTier: number;
  };
}

interface AssetTier {
  min: number;
  max: number;
  rate: number;
}

export function useMSRPCalculator(inputs: MSRPInputs): MSRPResult {
  return useMemo(() => {
    const BASE_FEE = 25000;

    // FQDN/Application pricing tiers
    const appTiers = [
      { max: 100, price: 750 },
      { max: 300, price: 500 },
      { max: 500, price: 400 },
      { max: 1000, price: 300 },
      { max: Infinity, price: 200 },
    ];

    // Frequency multipliers
    const frequencyMultipliers: Record<string, number> = {
      daily: 1.0,
      weekly: 0.55,
      monthly: 0.35,
    };

    // Calculate FQDN cost
    let fqdnCost = 0;
    let remaining = inputs.numberOfApplications || 0;
    let processed = 0;

    for (const tier of appTiers) {
      const appsInTier = Math.min(remaining, tier.max - processed);
      if (appsInTier > 0) {
        fqdnCost += appsInTier * tier.price;
        remaining -= appsInTier;
        processed += appsInTier;
      }
      if (remaining === 0) break;
    }

    const multiplier = frequencyMultipliers[inputs.scanSchedule] || 1;
    fqdnCost = Math.round(fqdnCost * multiplier);

    // Support tier pricing
    const supportTierPricing: Record<string, number> = {
      foundational: 0,
      professional: 50000,
      ultimate: 75000,
    };

    const supportCost =
      supportTierPricing[inputs.supportTier || 'foundational'] || 0;

    // Progressive asset pricing tiers (tax bracket style)
    // Each tier defines a range and the rate per asset in that range
    const assetPricingTiers: AssetTier[] = [
      { min: 0, max: 5000, rate: 10.0 }, // First 5K: $10/asset
      { min: 5000, max: 10000, rate: 5.0 }, // Next 5K: $5/asset
      { min: 10000, max: 25000, rate: 2.0 }, // Next 15K: $2/asset
      { min: 25000, max: 50000, rate: 1.8 }, // Next 25K: $1.80/asset
      { min: 50000, max: 300000, rate: 1.0 }, // Next 250K: $1/asset
      { min: 300000, max: Infinity, rate: 0.5 }, // Beyond 300K: $0.50/asset
    ];

    /**
     * Calculate progressive asset cost using tax bracket methodology.
     * Each asset is charged at its tier's rate, not the entire count at a flat rate.
     *
     * Example: 350,000 assets
     *   5,000 × $10.00 = $50,000     (0-5K tier)
     *   5,000 × $5.00  = $25,000     (5K-10K tier)
     *   15,000 × $2.00 = $30,000     (10K-25K tier)
     *   25,000 × $1.80 = $45,000     (25K-50K tier)
     *   250,000 × $1.00 = $250,000   (50K-300K tier)
     *   50,000 × $0.50 = $25,000     (300K+ tier)
     *   Total: $425,000
     */
    const calculateProgressiveAssetCost = (numAssets: number): number => {
      if (numAssets <= 0) return 0;

      let totalCost = 0;
      let remainingAssets = numAssets;

      for (const tier of assetPricingTiers) {
        const assetsInTier = Math.min(remainingAssets, tier.max - tier.min);
        if (assetsInTier > 0) {
          totalCost += assetsInTier * tier.rate;
          remainingAssets -= assetsInTier;
        }
        if (remainingAssets <= 0) break;
      }

      return totalCost;
    };

    // Calculate asset cost using progressive pricing (rounded to nearest dollar)
    const assetCost = Math.round(
      calculateProgressiveAssetCost(inputs.numberOfAssets || 0)
    );

    const totalMSRP = BASE_FEE + fqdnCost + assetCost + supportCost;

    return {
      msrp: totalMSRP,
      breakdown: {
        platformFee: BASE_FEE,
        fqdnCost,
        assetCost,
        supportTier: supportCost,
      },
    };
  }, [
    inputs.numberOfApplications,
    inputs.scanSchedule,
    inputs.supportTier,
    inputs.numberOfAssets,
  ]);
}

// Helper function to format currency
export function formatMSRP(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}
